import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled, { css } from 'styled-components';
import { blueLink, grey25, grey50, navy, white } from '@zocdoc/branding/colors';

import Arrow from '../icons/Arrow';
import { UnstyledLink } from '../link';
import { sharpSansBold, sharpSansSemibold, FontFamily } from '../typography';

const SIZE = {
    LARGE: 'btn-lg',
    MEDIUM: 'btn-med',
    SMALL: 'btn-small',
};

const STYLE = {
    PRIMARY: 'btn-primary',
    SECONDARY: 'btn-secondary',
    INVERTED: 'btn-inverted',
    TRANSPARENT: 'btn-trans',
    LINK: 'btn-link',
    SCROLL_UP_ARROW: 'btn-scroll-up-arrow',
    SCROLL_DOWN_ARROW: 'btn-scroll-down-arrow',
};

const buttonSize = ({ size }) => {
    switch (size) {
        case SIZE.LARGE:
            return css`
                ${sharpSansBold(32, 40)}
                padding: 7px 15px 7px;
            `;

        case SIZE.MEDIUM:
            return css`
                ${sharpSansSemibold(18, 28)}
                padding: 8px 15px 8px;
            `;

        case SIZE.SMALL:
            return css`
                ${sharpSansSemibold(15, 20)}
                padding: 9px 15px 9px;
            `;
    }
};

const buttonStyle = ({ disabled, style }) => {
    if (disabled) {
        switch (style) {
            case STYLE.TRANSPARENT:
                return css`
                    background-color: transparent;
                    border: 1px solid ${grey50};
                    color: ${grey50};
                    cursor: default;

                    &:hover,
                    &:active {
                        background-color: transparent;
                        border: 1px solid ${grey50};
                        color: ${grey50};
                        cursor: default;
                    }
                `;

            case STYLE.LINK:
                return css`
                    background-color: transparent;
                    border: none;
                    color: ${grey50};
                    cursor: default;
                    text-decoration: none;

                    &:hover,
                    &:active {
                        color: ${grey50};
                        cursor: default;
                        text-decoration: none;
                    }
                `;

            default:
                return css`
                    background-color: ${grey25};
                    border: 1px solid ${grey25};
                    color: ${white};
                    cursor: default;

                    &:hover,
                    &:active {
                        background-color: ${grey25};
                        border: 1px solid ${grey25};
                        color: ${white};
                        cursor: default;
                    }
                `;
        }
    }

    switch (style) {
        case STYLE.PRIMARY:
            return css`
                background-color: ${p => p.theme.components.button.primary.bgColor};
                border: 1px solid ${p => p.theme.components.button.primary.borderColor};
                color: ${p => p.theme.components.button.primary.color};
                &:hover,
                &:active {
                    background-color: ${p => p.theme.components.button.primary.hoverActiveBgColor};
                    border-color: ${p => p.theme.components.button.primary.hoverActiveBorderColor};
                    color: ${p => p.theme.components.button.primary.hoverActiveColor};
                    text-decoration: none;
                }
            `;

        case STYLE.SECONDARY:
            return css`
                background-color: ${white};
                border: 1px solid ${navy};
                color: ${navy};

                &:hover,
                &:active {
                    background-color: ${navy};
                    border-color: ${navy};
                    color: ${white};
                    text-decoration: none;
                }
            `;

        case STYLE.INVERTED:
            return css`
                background-color: ${p => p.theme.components.button.inverted.bgColor};
                border: 1px solid ${p => p.theme.components.button.inverted.borderColor};
                color: ${p => p.theme.components.button.inverted.color};
                &:hover,
                &:active {
                    background-color: ${p => p.theme.components.button.inverted.hoverActiveBgColor};
                    border-color: ${p => p.theme.components.button.inverted.hoverActiveBorderColor};
                    color: ${p => p.theme.components.button.inverted.hoverActiveColor};
                    text-decoration: none;
                }
            `;

        case STYLE.TRANSPARENT:
            return css`
                background-color: transparent;
                border: 1px solid ${navy};
                color: ${navy};

                &:hover,
                &:active {
                    background-color: ${navy};
                    border: 1px solid ${navy};
                    color: ${white};
                    text-decoration: none;
                }
            `;

        case STYLE.LINK:
            return css`
                background-color: transparent;
                border: none;
                color: ${blueLink};

                &:hover,
                &:active {
                    background-color: transparent;
                    border: none;
                    text-decoration: underline;
                }
            `;
        case STYLE.SCROLL_UP_ARROW:
        case STYLE.SCROLL_DOWN_ARROW:
            return css`
                font: 15px/1 ${FontFamily.SharpSansSemibold};
                background-color: ${p => p.theme.components.button.scroll.bgColor};
                border:none;
                outline: none;
                color: ${p => p.theme.components.button.scroll.textColor};
                position: absolute;
                padding-left: 25px;
                padding-bottom: 0px;

                &:hover,
                &:active {
                    background-color: ${p => p.theme.components.button.scroll.bgColor};
                    border: none;
                    outline: none;
                }
            `;
        default:
            return '';
    }
};

const buttonFluid = ({ fluid }) => {
    if (fluid) {
        return css`
            box-sizing: border-box;
            width: 100%;
        `;
    }
};

const ScrollArrowBtnContainer = styled.div`
    position: relative;
    height: 1.3em;
`;

const ScrollArrow = styled(Arrow)`
    position: absolute;
    bottom: 0;
    height: 0.5em;
    width: 1em;
`;

// Filter out the fluid, size, and style props so they don't get rendered as actual HTML attributes on the div
// https://github.com/styled-components/styled-components/issues/411#issuecomment-275594974
// eslint-disable-next-line no-unused-vars
const StyledButton = styled(({
    beforeClick, fluid, size, style, ...props
}) => {
    let button = null;
    // If the Button has an href, render it as an `a` tag rather than a `button`
    if (props.href) {
        button = (
            <UnstyledLink
                beforeClick={beforeClick}
                {...props}
            />
        );
    } else if (style === STYLE.SCROLL_UP_ARROW) {
        button = (
            <ScrollArrowBtnContainer>
                <ScrollArrow direction="up" />
                <button {...props} />
            </ScrollArrowBtnContainer>
        );
    } else if (style === STYLE.SCROLL_DOWN_ARROW) {
        button = (
            <ScrollArrowBtnContainer>
                <ScrollArrow direction="down" />
                <button {...props} />
            </ScrollArrowBtnContainer>
        );
    } else {
        button = <button {...props} />;
    }

    return button;
})`
    cursor: pointer;
    display: inline-block;
    min-width: 20px;
    text-align: center;
    text-decoration: none;
    ${buttonSize}
    ${buttonStyle}
    ${buttonFluid}
`;

export default class Button extends PureComponent {
    static SIZE = SIZE;
    static STYLE = STYLE;

    static propTypes = {
        beforeClick: PropTypes.func, // Only use with href
        children: PropTypes.any.isRequired,
        disabled: PropTypes.bool,
        fluid: PropTypes.bool,
        href: PropTypes.string,
        onClick: PropTypes.func,
        size: PropTypes.oneOf(Object.values(SIZE)),
        style: PropTypes.oneOf(Object.values(STYLE)),
        tabIndex: PropTypes.number,
    };

    static defaultProps = {
        size: SIZE.MEDIUM,
        style: STYLE.PRIMARY,
        tabIndex: 0,
    };

    render() {
        return (
            <StyledButton
                {...this.props}
                onClick={this.handleClick}
                tabIndex={this.props.disabled ? undefined : this.props.tabIndex}
            />
        );
    }

    handleClick = e => {
        if (this.props.disabled || !this.props.onClick) {
            return;
        }

        this.props.onClick(e);
    }
}
